import { useQuery } from '@tanstack/vue-query'
import { ActionRequestResponseDto, ActionRequestType } from '@torticity/types'
import orderBy from 'lodash/orderBy.js'

export function useActionRequestsQuery(
  filterById?: string | null,
  filterByStatus?: string[],
  filterBySignatureStatus?: string[],
  enabled?: boolean,
  documentGroupsOnly?: boolean,
) {
  const { $http, $flagsmith } = useNuxtApp()

  const route = useRoute()
  const isPublic = route.path.includes('public')

  async function getActionRequests(): Promise<ActionRequestResponseDto[]> {
    let actionRequests: ActionRequestResponseDto[]

    if (filterById) {
      actionRequests = await $http.$get(`/v1/action-requests/${filterById}`)
    } else {
      // Build query parameters for backend filtering
      const queryParams = new URLSearchParams()

      if (filterByStatus) {
        filterByStatus.forEach(status => queryParams.append('status', status))
      }

      if (filterBySignatureStatus) {
        filterBySignatureStatus.forEach(signatureStatus =>
          queryParams.append('signatureStatus', signatureStatus),
        )
      }

      const queryString = queryParams.toString()
      // Use document-groups endpoint when documentGroupsOnly is true
      const baseUrl = documentGroupsOnly
        ? '/v1/action-requests/document-groups'
        : '/v1/action-requests'
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl

      actionRequests = await $http.$get(url)

      // Only check feature flags for lists, not single actions
      // Single actions are checked in the RequestRouter component
      if (!$flagsmith.hasFeature('casewise_appointments')) {
        actionRequests = actionRequests.filter(
          action => action.type !== ActionRequestType.APPOINTMENT,
        )
      }
    }

    return actionRequests.length
      ? orderBy(actionRequests, ar => Number(ar.id), 'desc')
      : actionRequests
  }

  return useQuery({
    queryFn: getActionRequests,
    queryKey: [
      'action-requests',
      filterById,
      filterByStatus,
      filterBySignatureStatus,
      documentGroupsOnly,
    ],
    enabled: enabled !== undefined ? enabled : !isPublic,
  })
}
